import {useState, useEffect} from 'react';

import * as Entri from 'entrijs';

import {Button} from 'shells/button/button';
import {CogRegularIcon} from 'shells/icon';
import {Link} from 'shells/link/link';

import type {GetSharedInboxConfig_sharedInboxConfig_sharedAddresses_fromDomain as FromDomainConfig} from 'nutshell-graphql-types';

import {addNotification} from '../../../../../notifications/index';
import {useCreateEntriConfig} from '../../../../setup/domains/graphql/hooks';

import {VerificationState} from './consts';
import {DnsRecordList} from './dns-record-list';
import {DomainInfo} from './domain-info';
import {getIsReplyingVerified} from './helpers';
import {SetupSection} from './setup-section';

interface Props {
    emailAddress: string | undefined;
    domain: string | null | undefined;
    fromDomain: FromDomainConfig | null;
    onEntriComplete: () => void;
}

export function ReplyingSection(props: Props) {
    const {onEntriComplete} = props;

    const [isInEntriFlow, setIsInEntriFlow] = useState<boolean>(false);

    const {createEntriConfig, isCreating} = useCreateEntriConfig();

    const isVerified = getIsReplyingVerified(props.fromDomain);

    const handleStartSetup = () => {
        if (!props.domain) {
            return;
        }

        setIsInEntriFlow(true);
        createEntriConfig({domain: props.domain})
            .then((config) => {
                if (config) {
                    return Entri.showEntri(config);
                }

                setIsInEntriFlow(false);
                addNotification('', {
                    body: 'An unexpected error occurred. Please try again later.',
                    icon: 'frowny',
                });
            })
            .catch(() => {
                setIsInEntriFlow(false);
            });
    };

    useEffect(() => {
        const handleEntriClose = (event: CustomEvent) => {
            setIsInEntriFlow(false);

            if (event.detail.success) {
                onEntriComplete();
            }
        };

        window.addEventListener('onEntriClose', handleEntriClose as EventListener);

        return () => {
            window.removeEventListener('onEntriClose', handleEntriClose as EventListener);
        };
    }, [onEntriComplete]);

    return (
        <SetupSection
            title='Replying'
            verificationState={isVerified ? VerificationState.VERIFIED : VerificationState.IDLE}
            description='To reply to emails from a shared address, you need a valid domain. Nutshell can help you set it up if one was not detected.'
            headerAction={
                <Link icon={CogRegularIcon} variant='primary' size='small' href='/setup/domains'>
                    Go to Domains
                </Link>
            }
        >
            {props.domain && (
                <>
                    <DomainInfo emailAddress={props.emailAddress} domain={props.domain} />
                    {props.fromDomain && <DnsRecordList fromDomain={props.fromDomain} />}
                    {!isVerified && (
                        <div className='flex justify-center pad-16'>
                            <Button
                                variant='primary'
                                size='big'
                                onClick={handleStartSetup}
                                disabled={isCreating || isInEntriFlow}
                            >
                                Start setup
                            </Button>
                        </div>
                    )}
                </>
            )}
        </SetupSection>
    );
}
